import type {
  ComponentProps,
  ComponentPropsWithoutRef,
  ElementRef,
} from "react";
import { createContext, forwardRef, useContext } from "react";
import { Separator as SeparatorPrimitive } from "radix-ui";
import type { VariantProps } from "tailwind-variants";
import { tv } from "tailwind-variants";

import {
  Badge as BadgePrimitive,
  Button as ButtonPrimitive,
  Label as LabelPrimitive,
} from "@/shared/ui";

const detailCardVariants = tv({
  slots: {
    root: "flex flex-col gap-3 rounded-lg border border-l-8 bg-white p-3",
    header: "flex flex-row gap-1 pr-4",
    title: "leading-5 font-semibold",
    content: "flex grow flex-col gap-3 px-4",
    badge: "w-full",
    label: "text-xs text-neutral-600",
    description: "text-xs text-neutral-800",
    separator: "-mx-3 h-px bg-current",
    footer: "flex justify-between gap-4 px-4",
    button: "w-fit bg-neutral-200 p-1.5",
    thumbnail: "size-full min-h-24 w-24 bg-white object-cover",
  },
  variants: {
    color: {
      nature: {
        root: "border-nature-03",
        header: "text-nature-10",
        badge: "bg-primary-100 text-primary-900",
        separator: "bg-nature-03",
      },
      salmon: {
        root: "border-salmon-03",
        header: "text-salmon-10",
        badge: "border-salmon-03 bg-salmon-03 text-salmon-10",
        separator: "bg-salmon-03",
      },
      brown: {
        root: "border-neutral-300",
        header: "text-neutral-950",
        badge: "border-neutral-300 bg-neutral-300 text-neutral-950",
        separator: "bg-neutral-300",
      },
      "nature-dark": {
        root: "border-nature-04",
        header: "text-nature-10",
        badge: "border-nature-04 bg-nature-04 text-nature-10",
        separator: "bg-nature-04",
      },
      yellow: {
        root: "border-yellow-700",
        separator: "bg-yellow-200",
      },
    },
    variant: {
      borderLess: {
        root: "overflow-hidden border-0 bg-white",
        description: "inline-flex items-center gap-1 text-sm",
      },
    },
    badgeOutlined: {
      true: {
        badge: "bg-transparent",
      },
    },
  },

  compoundVariants: [
    {
      color: "nature",
      badgeOutlined: true,
      className: {
        badge: "border-primary-100 text-primary-950",
      },
    },
    {
      color: "salmon",
      badgeOutlined: true,
      className: {
        badge: "border-salmon-03 text-salmon-10",
      },
    },
    {
      color: "brown",
      badgeOutlined: true,
      className: {
        badge: "border-neutral-300 text-neutral-950",
      },
    },
    {
      color: "nature-dark",
      badgeOutlined: true,
      className: {
        badge: "border-nature-04 text-nature-10",
      },
    },
  ],
});

const {
  root,
  header,
  title,
  content,
  badge,
  button,
  label,
  description,
  separator,
  footer,
  thumbnail,
} = detailCardVariants();

const insetVariants = tv({
  variants: {
    side: {
      all: "-m-3",
      x: "-mx-3",
      y: "-my-3",
      top: "-mt-3",
      bottom: "-mb-3",
      left: "-ml-3",
      right: "-mr-3",
    },
    rightSpacing: {
      true: "mr-0",
    },
  },
  defaultVariants: {
    side: "all",
    rightSpacing: true,
  },
});
type InsetVariants = VariantProps<typeof insetVariants>;

type DetailCardContextValues = Pick<
  VariantProps<typeof detailCardVariants>,
  "color" | "variant"
>;

const DetailCardContext = createContext<DetailCardContextValues>({
  color: "nature",
});

const useDetailCardContext = () => useContext(DetailCardContext);

const Root = forwardRef<
  ElementRef<"div">,
  ComponentPropsWithoutRef<"div"> & DetailCardContextValues
>(({ className, color, variant, ...props }, ref) => (
  <DetailCardContext.Provider value={{ color, variant }}>
    <div ref={ref} className={root({ className, color, variant })} {...props} />
  </DetailCardContext.Provider>
));
Root.displayName = "DetailCard.Root";

const Header = forwardRef<
  ElementRef<"header">,
  ComponentPropsWithoutRef<"header">
>(({ className, ...props }, ref) => {
  const { color, variant } = useDetailCardContext();
  return (
    <header
      ref={ref}
      className={header({ className, color, variant })}
      {...props}
    />
  );
});
Header.displayName = "DetailCard.Header";

const Content = forwardRef<ElementRef<"div">, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => {
    return <div ref={ref} className={content({ className })} {...props} />;
  },
);
Content.displayName = "DetailCard.Content";

const Footer = forwardRef<
  ElementRef<"footer">,
  ComponentPropsWithoutRef<"footer">
>(({ className, ...props }, ref) => {
  return <footer ref={ref} className={footer({ className })} {...props} />;
});
Footer.displayName = "DetailCard.Footer";

const Title = forwardRef<ElementRef<"h4">, ComponentPropsWithoutRef<"h4">>(
  ({ className, children, ...props }, ref) => {
    const { color, variant } = useDetailCardContext();

    return (
      <h4 ref={ref} className={title({ className, color, variant })} {...props}>
        {children}
      </h4>
    );
  },
);
Title.displayName = "DetailCard.Title";

const Badge = forwardRef<
  ElementRef<typeof BadgePrimitive>,
  ComponentPropsWithoutRef<typeof BadgePrimitive>
>(({ className, size = "x-small", outline, ...props }, ref) => {
  const { color, variant: detailCardVariant } = useDetailCardContext();

  return (
    <BadgePrimitive
      ref={ref}
      size={size}
      className={badge({
        className,
        color: color,
        badgeOutlined: outline,
        variant: detailCardVariant,
      })}
      {...props}
    />
  );
});
Badge.displayName = "DetailCard.Badge";

const Button = forwardRef<
  ElementRef<typeof ButtonPrimitive>,
  ComponentPropsWithoutRef<typeof ButtonPrimitive>
>(({ className, ...props }, ref) => {
  return (
    <ButtonPrimitive
      ref={ref}
      variant="secondary"
      className={button({ className })}
      {...props}
    />
  );
});
Button.displayName = "DetailCard.Button";

const Separator = forwardRef<
  ElementRef<typeof SeparatorPrimitive.Root>,
  ComponentPropsWithoutRef<typeof SeparatorPrimitive.Root>
>(
  (
    { className, orientation = "horizontal", decorative = true, ...props },
    ref,
  ) => {
    const { color, variant } = useDetailCardContext();
    return (
      <SeparatorPrimitive.Root
        asChild
        ref={ref}
        decorative={decorative}
        orientation={orientation}
        className={separator({ className, color, variant })}
        {...props}
      >
        <span />
      </SeparatorPrimitive.Root>
    );
  },
);
Separator.displayName = "DetailCard.Separator";

const Label = forwardRef<
  ElementRef<typeof LabelPrimitive>,
  ComponentPropsWithoutRef<typeof LabelPrimitive>
>(({ className, ...props }, ref) => {
  const { variant } = useDetailCardContext();

  return (
    <LabelPrimitive
      ref={ref}
      className={label({ className, variant })}
      {...props}
    />
  );
});
Label.displayName = "DetailCard.Label";

const Description = ({ className, ...props }: ComponentProps<"div">) => {
  const { variant } = useDetailCardContext();

  return (
    <div
      data-slot="detail-card-description"
      className={description({ className, variant })}
      {...props}
    />
  );
};

const Inset = forwardRef<
  ElementRef<"div">,
  ComponentPropsWithoutRef<"div"> & InsetVariants
>(({ className, side, ...props }, ref) => (
  <div ref={ref} className={insetVariants({ side, className })} {...props} />
));
Inset.displayName = "DetailCard.Inset";

const Thumbnail = forwardRef<
  ElementRef<"img">,
  ComponentPropsWithoutRef<"img">
>(({ alt, className, ...props }, ref) => {
  return (
    <img
      ref={ref}
      alt={alt}
      className={thumbnail({ className })}
      draggable="false"
      {...props}
    />
  );
});
Thumbnail.displayName = "DetailCard.Thumbnail";

export const DetailCard = {
  Root,
  Header,
  Title,
  Footer,
  Content,
  Badge,
  Button,
  Description,
  Label,
  Separator,
  Inset,
  Thumbnail,
};
